import { useEffect, useRef, useState } from 'react';
import { bookingApi, BookingApiError } from '../../lib/api';
import { errorKeyFor, useT } from '../../lib/i18n';
import { useSession, useUi } from '../../lib/store';
import type { RequestCodeResult } from '../../lib/types';
import { PhoneField } from './PhoneField';

/**
 * Sign-in / registration in one sheet.
 *
 * There is no password anywhere in this flow: the customer proves they own an
 * address or number by entering a code sent to it, which is both the account
 * creation step and every subsequent login. The server decides whether a given
 * destination is a new registration; the only difference the customer sees is
 * being asked for their name once.
 *
 * The sheet never reveals whether an account already exists beyond that, so it
 * cannot be used to check who is a customer of the salon.
 *
 * WHICH FIELD IT ASKS FOR IS THE SERVER'S ANSWER, NOT A LOCAL GUESS.
 * `site.booking.otp_channel` is resolved per request — WhatsApp when the salon
 * has a working connection and an approved login template, e-mail otherwise —
 * so the sheet asks for a number only when a number can actually be messaged.
 *
 * ONE FAILURE HAS ITS OWN ROUTE OUT. A number with no WhatsApp account cannot
 * receive anything, and a sheet that only ever asked for a number would leave
 * that customer with nothing to try. When the server reports
 * `can_fallback_email` the sheet reveals an address field and asks again on
 * e-mail — the only channel switch it will ever make, and always downwards.
 */

type Phase = 'identify' | 'code';

export function AuthSheet({ slug }: { slug: string }): JSX.Element | null {
  const { t, lang } = useT();
  const open = useUi((s) => s.authOpen);
  const setOpen = useUi((s) => s.setAuthOpen);
  const site = useUi((s) => s.site);
  const signIn = useSession((s) => s.signIn);

  const [phase, setPhase] = useState<Phase>('identify');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<RequestCodeResult | null>(null);
  const [needsName, setNeedsName] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [countryIso, setCountryIso] = useState('');
  /** Set once the server says WhatsApp could not deliver — see the file note. */
  const [emailRescue, setEmailRescue] = useState(false);

  const firstFieldRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const portalChannel = site?.booking.otp_channel ?? 'email';
  // The rescue makes this sheet e-mail for the rest of the attempt, whatever
  // the portal's own channel is.
  const channel = emailRescue ? 'email' : portalChannel;
  const usesEmail = channel === 'email';

  useEffect(() => {
    if (!open) return;
    setPhase('identify');
    setCode('');
    setError(null);
    setChallenge(null);
    setNeedsName(false);
    setEmailRescue(false);
    const id = setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [open]);

  // The picker opens on the salon's country until the customer moves it. Not
  // in the reset above: a customer who corrected the country and came back to
  // fix a typo should not find it snapped back.
  useEffect(() => {
    if (!countryIso && site?.booking.default_country_iso) {
      setCountryIso(site.booking.default_country_iso);
    }
  }, [site?.booking.default_country_iso, countryIso]);

  useEffect(() => {
    if (phase === 'code') setTimeout(() => codeRef.current?.focus(), 60);
  }, [phase]);

  // Resend countdown.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Escape closes, and focus is trapped inside the dialog while it is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, setOpen]);

  if (!open) return null;

  function describe(err: unknown): string {
    if (err instanceof BookingApiError) {
      const key = errorKeyFor(err.code);
      // The server's own message is friendlier when we have no mapped copy.
      return key === 'err_generic' && err.message ? err.message : t(key);
    }
    return t('err_generic');
  }

  async function sendCode(forceEmail = emailRescue): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const byEmail = forceEmail || usesEmail;
      const result = await bookingApi.requestCode(slug, {
        ...(byEmail ? { email } : { phone }),
        // When the code goes by e-mail we still collect the phone on
        // registration: it is what links the account to a POS customer record,
        // and it is how the salon reaches someone about their appointment.
        ...(byEmail && phone.trim() ? { phone: phone.trim() } : {}),
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(countryIso ? { country_iso: countryIso } : {}),
        ...(forceEmail ? { channel: 'email' as const } : {}),
        locale: lang,
      });

      // Accepted but undeliverable. Staying on this step with the reason and an
      // address field is the only thing that helps: the code does not exist
      // anywhere the customer can reach, so moving them to the code entry
      // screen would just be asking them to type in a number they never got.
      if (!result.sent) {
        setChallenge(null);
        if (result.can_fallback_email) {
          setEmailRescue(true);
          setError(`${t('wa_undelivered')} ${result.send_error ?? ''}`.trim());
        } else {
          setError(result.send_error ?? t('err_generic'));
        }
        return;
      }

      setChallenge(result);
      setNeedsName(result.is_registration);
      setCooldown(result.resend_after_seconds);
      setPhase('code');
    } catch (err) {
      // A brand-new customer must supply a name before a code can be sent.
      if (err instanceof BookingApiError && err.code === 'NAME_REQUIRED') {
        setNeedsName(true);
        setError(t('new_account_note'));
      } else {
        setError(describe(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function verify(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const session = await bookingApi.verifyCode(slug, {
        // Exactly what sendCode sent, or the server looks the challenge up
        // under a destination it never issued a code to.
        ...(usesEmail ? { email } : { phone }),
        code: code.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(countryIso ? { country_iso: countryIso } : {}),
        ...(emailRescue ? { channel: 'email' as const } : {}),
      });
      signIn(slug, session.token, session.account);
      setOpen(false);
    } catch (err) {
      setError(describe(err));
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  const needsPhone = site?.booking.require_phone ?? false;
  const phoneValid = phone.replace(/\D/g, '').length >= 7;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const identifyValid = usesEmail ? emailValid : phoneValid;
  const canSend =
    identifyValid &&
    (!needsName || (name.trim().length >= 2 && (!usesEmail || !needsPhone || phoneValid)));

  const subKey = usesEmail ? 'auth_sub_email' : channel === 'sms' ? 'auth_sub_sms' : 'auth_sub_whatsapp';

  return (
    <div
      className="bk-fade fixed inset-0 z-50 flex items-end justify-center bg-zinc-900/40 sm:items-center"
      onClick={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        /*
         * Scrollable and viewport-bounded: on a landscape phone, or once
         * registration adds the name and phone fields, the sheet is taller
         * than the screen and the submit button would sit below the fold with
         * no way to reach it.
         */
        className="bk-sheet bk-sheet-panel bk-shadow w-full max-w-md overflow-y-auto overscroll-contain rounded-t-3xl bg-white p-5 sm:rounded-3xl sm:p-6"
        style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-start gap-3">
          <div className="flex-1">
            <h2 id="auth-title" className="text-lg font-semibold text-zinc-900">
              {t('auth_title')}
            </h2>
            <p className="mt-1 text-sm text-zinc-500">
              {phase === 'identify' ? t(subKey) : `${t('code_sent_to')} ${challenge?.destination_hint ?? ''}`}
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="bk-btn bk-btn-sm bk-btn-quiet -me-1.5 -mt-1.5 shrink-0"
            aria-label={t('close')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {error && (
          <p role="alert" className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
            {error}
          </p>
        )}

        {phase === 'identify' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSend && !busy) void sendCode();
            }}
          >
            {usesEmail ? (
              <label className="block">
                <span className="bk-label">{t('email_label')}</span>
                <input
                  ref={firstFieldRef}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className="bk-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </label>
            ) : (
              <PhoneField
                iso={countryIso}
                onIso={setCountryIso}
                value={phone}
                onValue={setPhone}
                inputRef={firstFieldRef}
              />
            )}

            {needsName && (
              <>
                <label className="mt-4 block">
                  <span className="bk-label">{t('name_label')}</span>
                  <input
                    type="text"
                    autoComplete="name"
                    className="bk-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </label>
                {usesEmail && needsPhone && (
                  // Same picker as the WhatsApp path, so an account created by
                  // e-mail still stores a number the salon can dial and that a
                  // later WhatsApp login will match.
                  <div className="mt-4">
                    <PhoneField iso={countryIso} onIso={setCountryIso} value={phone} onValue={setPhone} />
                  </div>
                )}
              </>
            )}

            <button type="submit" className="bk-btn bk-btn-primary mt-5 w-full" disabled={!canSend || busy}>
              {busy ? t('loading') : emailRescue ? t('send_to_email') : t('send_code')}
            </button>

            {emailRescue && (
              // A way back, in case the number was simply mistyped rather than
              // WhatsApp-less. Without it the rescue is a one-way door.
              <button
                type="button"
                className="bk-btn bk-btn-quiet mt-2 w-full py-1 text-xs"
                onClick={() => {
                  setEmailRescue(false);
                  setError(null);
                }}
              >
                {t('change_number')}
              </button>
            )}
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (code.trim().length >= 4 && !busy) void verify();
            }}
          >
            <label className="block">
              <span className="bk-label">{t('code_label')}</span>
              <input
                ref={codeRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d*"
                maxLength={6}
                className="bk-input text-center text-2xl font-semibold tracking-[0.5em]"
                dir="ltr"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />
            </label>

            {needsName && !name.trim() && (
              <label className="mt-4 block">
                <span className="bk-label">{t('name_label')}</span>
                <input
                  type="text"
                  autoComplete="name"
                  className="bk-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </label>
            )}

            <button
              type="submit"
              className="bk-btn bk-btn-primary mt-5 w-full"
              disabled={code.trim().length < 4 || busy}
            >
              {busy ? t('loading') : t('verify')}
            </button>

            <div className="mt-4 flex items-center justify-between text-xs">
              <button
                type="button"
                className="bk-btn bk-btn-quiet px-0 py-1"
                onClick={() => {
                  setPhase('identify');
                  setError(null);
                }}
              >
                {usesEmail ? t('change_details') : t('change_number')}
              </button>
              <button
                type="button"
                className="bk-btn bk-btn-quiet px-0 py-1 disabled:opacity-40"
                disabled={cooldown > 0 || busy}
                onClick={() => void sendCode()}
              >
                {cooldown > 0 ? `${t('resend_in')} ${cooldown}s` : t('resend')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
